import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, DoorOpen } from 'lucide-react';
import { useResellerContext } from '@/lib/reseller-context-store';
import { useCustomerContext } from '@/lib/customer-context-store';
import { useAuth } from '@/lib/auth-store';

/**
 * ResellerContextBanner
 * ─────────────────────────────────────────────────────────────────────
 * Shown only when a super_admin has entered a specific reseller context.
 * Sits above the CustomerContextBanner so the reseller/customer drill-down
 * reads top-to-bottom like breadcrumbs:
 *
 *     ┌──────────────────────────────────────────┐
 *     │  Inside reseller [Name]   [Exit]         │  ← this banner
 *     ├──────────────────────────────────────────┤
 *     │  Now editing customer [Name]   [Exit]    │  ← customer banner
 *     └──────────────────────────────────────────┘
 *
 * When the user exits the reseller, we also clear any entered customer
 * — stepping out of the reseller means the customer context (which
 * belongs to that reseller) is no longer valid.
 *
 * Hidden for reseller_owner and downstream roles — they already have an
 * implicit reseller scope (their own) from the auth store and don't
 * drill in from /resellers.
 */
export function ResellerContextBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const current = useResellerContext((s) => s.current);
  const exit = useResellerContext((s) => s.exit);
  const exitCustomer = useCustomerContext((s) => s.exit);
  const user = useAuth((s) => s.user);

  // Only super_admin uses the reseller drill-down. Silently ignore any
  // stale state for other roles.
  if (!current) return null;
  if (!user) return null;
  if (user.role !== 'super_admin') return null;

  function handleExit() {
    // Stepping out of the reseller invalidates any entered customer too.
    exitCustomer();
    exit();
    navigate('/resellers');
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="sticky top-0 z-20 bg-indigo-600 text-white shadow-[0_2px_0_0_rgba(49,46,129,0.5)]"
    >
      {/* Diagonal-stripe texture layer — subtle depth, mirrors the customer banner. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.6) 8px 9px)',
        }}
      />
      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 py-2.5">
          <div className="flex items-center gap-3 min-w-0">
            <span
              aria-hidden
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-sm bg-white/15 ring-1 ring-white/25"
            >
              <Building2 className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.22em] opacity-80 leading-none">
                {t('reseller.insideReseller')}
              </div>
              <div className="mt-1 text-sm font-semibold leading-tight truncate" dir="auto">
                {current.name}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleExit}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-md border border-white/30 bg-white/10 px-3 py-1.5 text-xs font-medium tracking-wide transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-600"
            aria-label={`${t('reseller.exitToResellers')} — ${current.name}`}
          >
            <DoorOpen className="h-3.5 w-3.5" />
            <span>{t('reseller.exitToResellers')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
